import React, { FC, useState, useMemo } from 'react';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { Pagination, Keyboard, A11y } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { Box } from '@mui/material';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';

import 'swiper/css';
import 'swiper/css/pagination';

interface CarouselProps {
  items: any[];
  defIndex?: string;
  dataId?: string;
}

const rememberDataIndex = (dataId: string, slideIndex: number) => {
  if (dataId !== '0' && slideIndex >= 0) {
    localStorage.setItem('slide_index', String(slideIndex));
  }
};

const getRememberedIndex = (dataId: string, defIndex?: string): number => {
  if (dataId && dataId !== '0') {
    const savedId = localStorage.getItem('slider_data');
    if (savedId === dataId) {
      const savedIndex = localStorage.getItem('slide_index');
      if (savedIndex !== null) {
        const parsed = parseInt(savedIndex, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          return parsed;
        }
      }
    } else {
      localStorage.setItem('slide_index', '0');
      localStorage.setItem('slider_data', String(dataId));
    }
  }
  const def = defIndex ? parseInt(defIndex, 10) : 0;
  return !isNaN(def) && def >= 0 ? def : 0;
};

interface NavigationArrowProps {
  direction: 'prev' | 'next';
  disabled: boolean;
}

const CustomNavigationButton: FC<NavigationArrowProps> = ({ direction, disabled }) => {
  const swiper = useSwiper();

  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (direction === 'prev') {
          swiper.slidePrev();
        } else {
          swiper.slideNext();
        }
      }}
      aria-label={direction === 'prev' ? 'Предыдущий слайд' : 'Следующий слайд'}
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0 : 0.85,
        visibility: disabled ? 'hidden' : 'visible',
        pointerEvents: disabled ? 'none' : 'auto',
        color: '#09090b',
        padding: 0,
        outline: 'none',
        transition: 'opacity 0.2s ease, background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          backgroundColor: '#ffffff',
          color: '#740BB6',
          opacity: 1,
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
        },
      }}
    >
      {direction === 'prev' ? (
        <NavigateBeforeRoundedIcon sx={{ fontSize: 24, pointerEvents: 'none' }} />
      ) : (
        <NavigateNextRoundedIcon sx={{ fontSize: 24, pointerEvents: 'none' }} />
      )}
    </Box>
  );
};

const Carousel: FC<CarouselProps> = ({ items = [], defIndex = '0', dataId = '0' }) => {
  const initialIndex = useMemo(() => getRememberedIndex(dataId, defIndex), [dataId, defIndex]);
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex);

  if (!items || items.length === 0) {
    return null;
  }

  const hasMultiple = items.length > 1;

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        userSelect: 'none',
        overflow: 'hidden',
        '& .swiper': {
          width: '100%',
          height: '100%',
        },
        '& .swiper-slide': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          overflow: 'hidden',
        },
        '& .swiper-pagination': {
          bottom: '12px !important',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '6px',
          zIndex: 10,
        },
        '& .swiper-pagination-bullet': {
          width: '7px',
          height: '7px',
          backgroundColor: 'rgba(255, 255, 255, 0.55)',
          opacity: 1,
          borderRadius: '4px',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          margin: '0 !important',
          cursor: 'pointer',
        },
        '& .swiper-pagination-bullet-active': {
          width: '22px',
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
        },
      }}
    >
      <Swiper
        modules={[Pagination, Keyboard, A11y]}
        initialSlide={initialIndex}
        spaceBetween={0}
        slidesPerView={1}
        grabCursor={hasMultiple}
        keyboard={{ enabled: true }}
        pagination={
          hasMultiple
            ? {
                clickable: true,
                dynamicBullets: items.length > 5,
              }
            : false
        }
        onSlideChange={(swiper: SwiperType) => {
          setActiveIndex(swiper.activeIndex);
          rememberDataIndex(dataId, swiper.activeIndex);
        }}
      >
        {items.map((item, index) => (
          <SwiperSlide key={index}>{item}</SwiperSlide>
        ))}

        {hasMultiple && (
          <>
            {/* Prev Arrow Overlay (centered vertically via flexbox, zero translateY) */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 12,
                display: 'flex',
                alignItems: 'center',
                zIndex: 10,
                pointerEvents: 'none',
                '& > button': {
                  pointerEvents: 'auto',
                },
              }}
            >
              <CustomNavigationButton direction="prev" disabled={activeIndex === 0} />
            </Box>

            {/* Next Arrow Overlay (centered vertically via flexbox, zero translateY) */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: 12,
                display: 'flex',
                alignItems: 'center',
                zIndex: 10,
                pointerEvents: 'none',
                '& > button': {
                  pointerEvents: 'auto',
                },
              }}
            >
              <CustomNavigationButton direction="next" disabled={activeIndex >= items.length - 1} />
            </Box>

            {/* Slide counter badge (top-right) */}
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 10,
                backgroundColor: 'rgba(15, 23, 42, 0.72)',
                color: '#ffffff',
                borderRadius: '20px',
                px: '10px',
                py: '3px',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                pointerEvents: 'none',
                userSelect: 'none',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
              }}
            >
              {activeIndex + 1} / {items.length}
            </Box>
          </>
        )}
      </Swiper>
    </Box>
  );
};

export default Carousel;
